import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../lib/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    const stored = localStorage.getItem('user');
    return stored ? JSON.parse(stored) : null;
  });
  const [enabledModules, setEnabledModules] = useState(() => {
    const stored = localStorage.getItem('enabled_modules');
    return stored ? JSON.parse(stored) : [];
  });
  const [loading, setLoading] = useState(true);

  const isPlatformAdmin = user?.role === 'platform_admin';

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      // Always refresh from server when a token exists — fixes stale localStorage
      // (e.g., if modules changed server-side after a previous login).
      authAPI.me().then((res) => {
        const userData = res.data;
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
        setEnabledModules(userData.enabled_modules || []);
        localStorage.setItem('enabled_modules', JSON.stringify(userData.enabled_modules || []));
      }).catch(() => {
        logout();
      }).finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const refreshModules = useCallback(async () => {
    try {
      const res = await authAPI.me();
      const userData = res.data;
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
      setEnabledModules(userData.enabled_modules || []);
      localStorage.setItem('enabled_modules', JSON.stringify(userData.enabled_modules || []));
    } catch (e) {
      // ignore; interceptor handles 401
    }
  }, []);

  const login = useCallback(async (email, password) => {
    const res = await authAPI.login(email, password);
    const { access_token, user: userData, enabled_modules: modules } = res.data;
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('enabled_modules', JSON.stringify(modules || []));
    setUser(userData);
    setEnabledModules(modules || []);
    return userData;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('enabled_modules');
    setUser(null);
    setEnabledModules([]);
  }, []);

  const hasModule = useCallback(
    (moduleKey) => enabledModules.includes(moduleKey),
    [enabledModules],
  );

  return (
    <AuthContext.Provider value={{ user, enabledModules, isPlatformAdmin, loading, login, logout, hasModule, refreshModules }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
